import { currentYear } from "@/lib/siteYear";
import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { Leaf, Mail, Facebook, Instagram, Twitter, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FooterAd } from "@/components/AdSense";
import PreferredSourceButton from "@/components/PreferredSourceButton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { z } from "zod";

const emailSchema = z.string().email("Please enter a valid email address");

const Footer = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Anti-spam honeypot field
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check - if filled, it's a bot
    if (honeypot) {
      // Silently reject bot submissions
      toast({
        title: "Subscribed!",
        description: "Thank you for subscribing to our newsletter.",
      });
      return;
    }
    
    // Validate email
    const validation = emailSchema.safeParse(email);
    if (!validation.success) {
      toast({
        title: "Invalid email",
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    // Basic rate limiting via timing check (bots submit too fast)
    const formLoadTime = (window as any).__subscribeFormLoadTime;
    if (formLoadTime && Date.now() - formLoadTime < 2000) {
      // Form submitted in less than 2 seconds - likely a bot
      toast({
        title: "Subscribed!",
        description: "Thank you for subscribing to our newsletter.",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from("subscribers")
        .insert({ 
          email: email.trim().toLowerCase(),
          first_name: firstName.trim() || null,
          last_name: lastName.trim() || null,
        });

      if (error) {
        if (error.code === "23505") {
          toast({
            title: "Already subscribed",
            description: "This email is already on our mailing list.",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "Subscribed!",
          description: "Thank you for subscribing to our newsletter.",
        });
        setEmail("");
        setFirstName("");
        setLastName("");
      }
    } catch (error: any) {
      console.error("Subscription error:", error);
      toast({
        title: "Subscription failed",
        description: "Something went wrong. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Track form load time for timing-based bot detection
  useEffect(() => {
    (window as any).__subscribeFormLoadTime = Date.now();
  }, []);

  const footerSections = [
    {
      title: "Garden Guides",
      links: [
        { name: "How-To Guides", href: "/category/how-to" },
        { name: "Plant Care", href: "/category/plants" },
        { name: "Flower Guides", href: "/category/flowers" },
        { name: "Grow Guides", href: "/category/grow-guides" },
        { name: "All Articles", href: "/articles" },
      ],
    },
    {
      title: "Popular Topics",
      links: [
        { name: "Garden Design", href: "/category/garden-design" },
        { name: "Vegetables", href: "/category/grow-guides?sub=tomatoes" },
        { name: "Roses", href: "/category/flowers?sub=roses" },
        { name: "Indoor Plants", href: "/category/plants?sub=indoor-plants" },
        { name: "Reviews", href: "/category/reviews" },
      ],
    },
    {
      title: "Tools & Equipment",
      links: [
        { name: "Lawn Mowers", href: "/category/reviews?sub=lawn-mowers" },
        { name: "Best Leaf Blowers UK", href: "/guides/best-leaf-blowers-uk" },
        { name: "Hedge Trimmers", href: "/category/reviews?sub=hedge-trimmers" },
        { name: "Hand Tools", href: "/category/reviews?sub=secateurs" },
        { name: "Power Tools", href: "/reviews/power-tools" },
        { name: "Generators", href: "/generators" },
        { name: "Pressure Washers", href: "/pressure-washers" },
        { name: "Water Pumps", href: "/water-pumps" },
        { name: "JCB Tools", href: "/brand/jcb-tools" },
        { name: "All Reviews", href: "/category/reviews" },
      ],
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "/about" },
        { name: "Submit Content", href: "/submit-content" },
        { name: "Contact", href: "/contact" },
        { name: "Privacy Policy", href: "/privacy" },
        { name: "Terms of Service", href: "/terms" },
      ],
    },
  ];

  return (
    <>
      
      <footer className="bg-foreground text-background">
      <div className="container mx-auto px-4 lg:px-8 py-12">
        {/* Newsletter Section */}
        <div className="bg-background/5 border border-background/10 rounded-xl p-8 mb-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Stay Growing with Us</h3>
            <p className="text-primary-foreground/90 mb-6">
              Get weekly gardening tips, seasonal guides, and exclusive plant care advice 
              delivered straight to your inbox.
            </p>
            <form onSubmit={handleSubscribe} className="space-y-4 max-w-md mx-auto">
              {/* Honeypot field - hidden from users, visible to bots */}
              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                className="absolute -left-[9999px] opacity-0 pointer-events-none"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <div className="flex flex-col sm:flex-row gap-4">
                <label htmlFor="newsletter-first-name" className="sr-only">First name</label>
                <Input 
                  id="newsletter-first-name"
                  name="firstName"
                  type="text" 
                  placeholder="First name"
                  aria-label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="bg-primary-foreground text-foreground border-primary-foreground/20"
                  disabled={isLoading}
                />
                <label htmlFor="newsletter-last-name" className="sr-only">Last name</label>
                <Input 
                  id="newsletter-last-name"
                  name="lastName"
                  type="text" 
                  placeholder="Last name"
                  aria-label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="bg-primary-foreground text-foreground border-primary-foreground/20"
                  disabled={isLoading}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                <Input 
                  id="newsletter-email"
                  name="email"
                  type="email" 
                  placeholder="Enter your email"
                  aria-label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-primary-foreground text-foreground border-primary-foreground/20 flex-1"
                  disabled={isLoading}
                  required
                />
                <Button 
                  type="submit"
                  variant="secondary" 
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    "Subscribe"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <Leaf className="h-8 w-8" />
              <span className="text-xl font-bold">Garden-Review</span>
            </Link>
            <p className="text-primary-foreground/90 text-sm leading-relaxed mb-4">
              Your trusted companion for all things gardening. From seed to harvest, 
              we're here to help your garden thrive.
            </p>
            <div className="flex space-x-4">
              <Button size="sm" variant="ghost" className="p-2 hover:bg-primary-foreground/10" aria-label="Facebook">
                <Facebook className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" className="p-2 hover:bg-primary-foreground/10" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" className="p-2 hover:bg-primary-foreground/10" aria-label="Twitter">
                <Twitter className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" className="p-2 hover:bg-primary-foreground/10" aria-label="Email">
                <Mail className="h-4 w-4" />
              </Button>
            </div>
            <PreferredSourceButton variant="light" className="mt-4" />
          </div>

          {/* Footer Links */}
          {footerSections.map((section, index) => (
            <div key={index}>
              <h4 className="font-semibold text-primary-foreground mb-4">
                {section.title}
              </h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <Link 
                      to={link.href}
                      className="text-primary-foreground/90 hover:text-primary-foreground transition-colors text-sm"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/20 pt-8">
          <p className="text-primary-foreground/80 text-sm text-center mb-4">
            Garden-Review is a trading name of{" "}
            <a
              href="https://www.media-m.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary-foreground"
            >
              Media-M Global Ltd
            </a>
            , Suite 693, 80A Ruskin Ave, Welling DA16 3QQ, UK.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-primary-foreground/80 text-sm">
              © {currentYear()} Garden-Review. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm">
              <Link to="/privacy" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                Terms of Service
              </Link>
              <Link to="/cookies" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                Cookie Settings
              </Link>
              <Link to="/browse" className="text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                Browse All Pages
              </Link>
            </div>
          </div>
        </div>
        </div>
      </footer>
    </>
  );
};

export default Footer;
